<script module>
		import { user, game, view, send_data, fetch_data } from "../client.svelte.js";
		import FshpsCatalog from "./FshpsCatalog.svelte";
		import Fellowship from "./Fellowship.svelte";
		import Tales from "./Tales.svelte";
		import Tale from "./Tale.svelte";
		import Survey from "./Survey.svelte";
		import Upgrades from "./Upgrades.svelte";
	import {notEmpty} from "../core.js";

		export const gameLayouts = [
				{page: 'catalog', Page: FshpsCatalog, title: "Gruppenübersicht"},
				{page: 'fellowship', Page: Fellowship, title: "Gruppe", label: "Gruppe", icon: 'users'},
				{page: 'overview', Page: Survey, title: "Fertigkeiten der Gruppe", label: "Übersicht", icon: 'list'},
				{page: 'tales', Page: Tales, title: "Gespielte Abenteuer", label: "Gespielte Abenteuer", icon: 'scroll'},
				{page: 'improvements', Page: Upgrades, title: "Verbesserungen der Gruppe", label: "Verbesserungen", icon: 'arrow-up', admin: true},
				{page: 'tale', Page: Tale, title: "Abenteuer der Gruppe"},
			].map(layout => Object.assign(layout, {module: 'game'}))

		/*** selection and shared data of the narrate module (Gruppen-Historie) ***/
		export const narrate = $state({fshpID: null, taleID: null, fellows: []})
		let fshpFetching = null
		let fellowsLoading = null

		/** label and tag style of the tale states (0 = in preparation ... 3/4 = completed) **/
		export const taleStates = {
			0: {label: "vorbereitet", tag: 'outline'},
			1: {label: "unterbrochen", tag: 'accent'},
			2: {label: "läuft", tag: 'accent'},
			3: {label: "abgeschlossen", tag: 'neutral'},
			4: {label: "abgeschlossen", tag: 'neutral'} }

		export const is_admin = (fshp) => !!fshp && fshp.admin === user.ID
		export const can_start = (tale) => !!tale && tale.state < 3 && tale.master === user.ID
		export const tales_of = (fshpID) => user.tales.values().filter(tale => tale.FSHP === fshpID).toArray().sort((a, b) => b.NUM - a.NUM)

		export const fellow_name = (userID) => narrate.fellows.find(fellow => fellow.ID === userID)?.name ?? userID

		/** heroes of a player which are part of the fellowship (or all of his heroes, if none is assigned yet) **/
		export function fellow_heroes(userID, fshp) {
				const heroes = narrate.fellows.find(fellow => fellow.ID === userID)?.heroes ?? []
				const fshpHeroes = heroes.filter(hero => fshp?.heroes?.includes(hero.ID))
				return fshpHeroes.map(hero => hero.name).join(", ")
		}

		/** selects a fellowship; fetches it first if it is missing or only present as snippet **/
		export async function select_fellowship(fshpID) {
				const fshp = user.fellowships.get(fshpID)
				narrate.fshpID = fshpID
				if (fshp && !fshp.TYPE?.includes("Snippet")) {
					fetch_data(`/fellowship/${fshpID}`)
					fshpFetching = view.fellowship = fshp		// no promise -> {#await} shows {:then} immediately
				} else {
					// fshpFetching = fetch_data(`/fellowship/${fshpID}`)
					// 	.then(() => {
					// 		view.fellowship = user.fellowships.get(fshpID);
					// 		console.log(`NARRATE - fellowship ${fshpID} fetched from server`, view.fellowship)
					// 	})
					const options = Object.assign({method: 'GET'},  {mode: 'same-origin', cache: 'no-cache'})
					fshpFetching = fetch(`/fellowship/${fshpID}`, options)
						.then(() => {
							view.fellowship = user.fellowships.get(fshpID);
							console.log(`NARRATE - fellowship ${fshpID} fetched from server`, view.fellowship)
						})
				}
				console.log(`NARRATE - fetching`, $state.snapshot(fshpFetching))
				return await fshpFetching
		}

		export async function open_tale(taleID) {
				narrate.taleID = taleID
				view.switch_layout('game-tale')
				await fetch_data(`/tale/${taleID}`)		// replaces the snippet by the full tale
		}

		export async function create_fellowship(name) {
				const response = await send_data('/fellowship/_', {name: name.trim()}, 'POST')
				const fshpID = response.fellowships?.[0]?.ID
				if (fshpID)  view.select_fellowship(fshpID)
		}

		export const upload_fshpImage = (fshp, file) => send_data(`/fellowship/${fshp.ID}/image`, {}, 'POST', file)

		export async function create_tale(fshpID, advtID) {
				await send_data('/tale/_', {fshpID, advtID}, 'POST')
				view.switch_layout(`game-tales`)
		}

		export const start_tale = (tale) => game.commence(tale.ID)
</script>


<script>
		import Dialog from "../elements/Dialog.svelte";
		import Breadcrumbs from "../elements/Breadcrumbs.svelte";
		import Help from "../elements/Help.svelte";
		import Sidebar from "../elements/Sidebar.svelte";
		import ModuleTabs from "../elements/ModuleTabs.svelte";

		let page = $derived(view.layout.page)
		let fshp = $derived(view.fellowship)
		let tale = $derived(user.tales.get(narrate.taleID))
		let tabs = $derived(gameLayouts.filter(layout => layout.label && (!layout.admin || is_admin(fshp)))
			.map(layout => ({id: layout.page, label: layout.label, icon: layout.icon, info: layout.title})))
		let adventures = $derived(user.adventures.values().toArray().sort((a, b) => a.ID.localeCompare(b.ID)))

		let newName = $state("")
		let selAdvt = $state(null)
		let Page = $derived(view.layout.Page)

		/** breadcrumbs: path from the fellowship catalog to the current page **/
		let trail = $derived.by(() => {
				if (page === 'catalog' || !fshp)
					return {crumbs: [], current: "Gruppen-Historie"}
				const root = {label: "Gruppen-Historie", onclick: () => view.switch_layout('game-catalog')}
				if (page === 'tale')
					return {crumbs: [root, {label: fshp.name, onclick: () => view.switch_layout(`game-tales`)}], current: tale?.name}
				return {crumbs: [root], current: fshp.name}
		})

		const show_dialog = (dialogID) => document.getElementById(dialogID)?.showModal()

		/** page actions in the sidebar **/
		let sideItems = $derived(
			(page === 'catalog') ?
				[{id: 'newfshp', icon: 'plus', label: "Gruppe anlegen", primary: true,
				  onclick: () => { newName = ""; show_dialog('new-fshp-dialog') }}] :
			(page === 'tale' && tale) ?
				[{id: 'start', icon: 'play', primary: true, disabled: !can_start(tale), onclick: () => start_tale(tale),
				  label: (tale.state === 1) ? "Abenteuer fortsetzen" : "Abenteuer starten",
				  title: (can_start(tale)) ? null : (tale.state >= 3) ? "Dieses Abenteuer ist abgeschlossen." : "Nur der Meister kann dieses Abenteuer starten."}] :
			(page !== 'tale' && fshp) ?
				[{id: 'newtale', icon: 'plus', label: "Abenteuer anlegen", primary: true,
				  onclick: () => { selAdvt = null; show_dialog('new-tale-dialog') }}] :
				[] )
</script>


<!-------------------------------- HTML -------------------------------------->

<main id='narrate'>

	<Breadcrumbs back={page !== 'catalog'} crumbs={trail.crumbs} current={trail.current} />

	<Sidebar items={sideItems} />

	{#if page !== 'catalog' && fshp}
		<ModuleTabs {tabs} selected={(page === 'tale') ? 'tales' : page} onselect={(tabPage) => view.switch_layout(`game-${tabPage}`)} />
	{/if}

	{#await narrate.fshpLoading}
		<div class="fetching">Gruppe {narrate.fshpID} wird geladen …</div>
	{:then}
		<Page/>
	{:catch}
		<div class="fetching">Gruppe {narrate.fshpID} konnte nicht geladen werden.</div>
	{/await}

	<Dialog id='new-fshp-dialog' title="Neue Gruppe anlegen"
	        actions={[{id: 'cancel', label: "Abbrechen"},
	                  {id: 'create', label: "Gruppe anlegen", symbol: 'plus', shown: newName.trim() !== "", func: () => create_fellowship(newName)}]}>
		<div class="dlg-body dlg-narrow">
			<div class="field">
				<label for="new-fshp-name" data-help="newfshp">Name der Gruppe</label>
				<input id="new-fshp-name" data-help="newfshp" placeholder="z. B. Die Grauen Wanderer" bind:value={newName}>
			</div>
			<p class="card-meta">Du wirst Admin der neuen Gruppe. Mitglieder, Bild und Aufenthaltsort kannst Du im nächsten Schritt festlegen.</p>
		</div>
	</Dialog>

	<Dialog id='new-tale-dialog' title="Neues Abenteuer für {fshp?.name ?? 'die Gruppe'}"
	        actions={[{id: 'cancel', label: "Abbrechen"},
	                  {id: 'create', label: "Abenteuer anlegen", symbol: 'plus', shown: !!selAdvt, func: () => create_tale(fshp.ID, selAdvt)}]}>
		<div class="dlg-body dlg-narrow">
			<p>Wähle das Abenteuer aus, das die Gruppe spielen soll. Du wirst Meister dieses Abenteuers.</p>
			{#each adventures as advt}
				<label class="radioRow" data-help="newtale">
					<input type="radio" name="new-tale-advt" value={advt.ID} bind:group={selAdvt}>
					<span>
						<span>{advt.ID} – {advt.name}</span>
						<span class="card-meta">{[advt.region, advt.campaigne].filter(Boolean).join(" · ")}</span>
					</span>
				</label>
			{:else}
				<p class="card-meta">Du hast noch keine eigenen Abenteuer. Lege zuerst im Modul „Abenteuer vorbereiten“ ein Abenteuer an.</p>
			{/each}
		</div>
	</Dialog>

	<Help/>

</main>


<!-------------------------------- STYLES -------------------------------------->

<style lang='sass'>
	@use "../colors" as colors

	#narrate
		display: grid
		grid-template-columns: var(--menu-width) minmax(0, 1fr) var(--help-width, 0)
		grid-template-rows: 51px auto minmax(0, 1fr)
		grid-template-areas: "panel mnav help" "panel head help" "panel main help"

		/* content of the pages (FshpsCatalog, Fellowship, Survey, Tales, Tale, Upgrades) */
		:global(article)
			padding: 18px 24px 32px
			display: flex
			flex-direction: column
			gap: 14px

		:global(.page-title)
			margin: 0 0 4px
			font-size: 20px

	.fetching
		position: fixed
		top: 50%
		left: 50%
		transform: translate(-50%, -50%)
		z-index: 10
		padding: 16px 24px
		border-radius: 8px
		background-color: colors.$primary-040-black
		color: colors.$white
		font-size: 16px

</style>
